// Modules
import { useState } from "react"
import { useMutation } from "react-query"

// Constants
import config from "~constants/config"

// Context
import { useAuthContext } from "~context/authContext"

// Services
import CommunicationService from "~services/CommunicationService"

// Components
import RequestCreationModal from "~components/RequestCreationModal"

// Interfaces
import { PerkShape } from "~shapes/perk"
import { OnRequestCloseProps } from "~shapes/modal"
import { ContactUsPayloadShape } from "~shapes/contact-us"

interface PerkRequestModalProps {
  onRequestClose: (props: OnRequestCloseProps) => void
  perk?: PerkShape
}

interface FormDataShape {
  subject: string
  message: string
}

const PerkRequestModal = ({ onRequestClose, perk }: PerkRequestModalProps) => {
  const { loggedUser, isLoading: authLoading } = useAuthContext()

  // Don't render if still loading or user is not loaded yet
  if (authLoading || !loggedUser) {
    return null
  }

  const { isLoading, isSuccess, mutateAsync } = useMutation(
    "contact-us",
    (payload: ContactUsPayloadShape) =>
      CommunicationService.postContactUs(payload)
  )

  const [errorMessage, setErrorMessage] = useState<string>(null!)

  const handleSave = async (formData: FormDataShape) => {
    let messageBody: string

    if (perk) {
      messageBody = `Perk Link: ${config.APP_MAIN_URL}/app/perks/${perk.id}. Message: ${formData.message}`
    } else {
      messageBody = formData.message
    }

    const payload: ContactUsPayloadShape = {
      to: {
        name: config.APP_NAME,
        mail: config.source_email,
      },
      from: {
        name: config.APP_NAME,
        mail: config.source_email,
      },
      replyTo: {
        name: loggedUser.name,
        mail: loggedUser.userName,
      },
      subject: `Message via Perk Central - ${!!perk ? "Update" : "New"} Perk`,
      type: "CONTACT_US",
      parameters: {
        from: loggedUser.name,
        type: formData.subject,
        body: messageBody,
      },
      isHtml: true,
    }

    try {
      await mutateAsync(payload)
    } catch (error) {
      setErrorMessage(error.message)
    }
  }

  return (
    <RequestCreationModal
      isLoading={isLoading}
      onSuccess={isSuccess}
      modalText={
        !perk
          ? "To create a new perk, contact the Stockperks Administrator team"
          : "To edit this perk, contact the Stockperks Administrator team"
      }
      modalTitle={!perk ? "Create a new perk" : "Edit perk"}
      errorMessage={errorMessage}
      handleSave={handleSave}
      onRequestClose={onRequestClose}
    />
  )
}

export default PerkRequestModal
